import { audioEngine } from '../utils/audioEngine';
import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Star } from 'lucide-react';
import { TUTORIAL_STEPS } from '../utils/tutorialData';
import { cn } from '../lib/utils';
import Prism3D from './Prism3D';
import { useTranslation } from 'react-i18next';

interface TutorialOverlayProps {
  step: number;
  onNext: () => void;
  onReset: () => void;
  onBack?: () => void;
  currentScreen: string;
  onSkip?: () => void;
  onNextMission?: () => void;
  user?: any;
}

const PAD = 10; // padding around the highlighted element

const ShellTile = ({ value, isSelected, isBlue, isFever }: { value: number, isSelected?: boolean, isBlue?: boolean, isFever?: boolean }) => {
  return (
    <div className={cn(
      "w-full h-full relative flex items-center justify-center transition-all duration-300",
      isSelected && "scale-110"
    )}>
      {/* Shell Shape (SVG Path) */}
      <svg viewBox="0 0 100 100" className={cn(
        "absolute inset-0 w-full h-full drop-shadow-[0_2px_4px_rgba(0,0,0,0.1)]",
        isSelected ? "drop-shadow-[0_0_12px_rgba(251,191,36,0.6)]" : ""
      )}>
        <defs>
          <linearGradient id="whiteShellGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="100%" stopColor="#f1f5f9" />
          </linearGradient>
          <linearGradient id="blueShellGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#38bdf8" />
            <stop offset="100%" stopColor="#0ea5e9" />
          </linearGradient>
          <linearGradient id="feverShellGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#f43f5e" />
            <stop offset="100%" stopColor="#881337" />
          </linearGradient>
          <linearGradient id="selectedShellGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#fbbf24" />
            <stop offset="100%" stopColor="#f59e0b" />
          </linearGradient>
        </defs>
        <path 
          d="M50 5 L85 25 L95 60 L75 90 L50 95 L25 90 L5 60 L15 25 Z" 
          fill={isSelected ? "url(#selectedShellGrad)" : (isFever ? "url(#feverShellGrad)" : (isBlue ? "url(#blueShellGrad)" : "url(#whiteShellGrad)"))}
          className="transition-colors duration-300"
          stroke={isSelected ? "#fbbf24" : "#e2e8f0"}
          strokeWidth="2"
        />
        {/* Ridge details */}
        <path d="M50 5 L50 95" stroke="black" strokeWidth="1" strokeOpacity="0.05" fill="none" />
        <path d="M25 15 L35 92" stroke="black" strokeWidth="1" strokeOpacity="0.03" fill="none" />
        <path d="M75 15 L65 92" stroke="black" strokeWidth="1" strokeOpacity="0.03" fill="none" />
      </svg>
      {/* Value Overlay */}
      <span className={cn(
        "relative z-10 font-num text-[11px] font-black",
        isSelected ? "text-slate-900" : (isFever || isBlue ? "text-white" : "text-slate-700")
      )}>
        {value}
      </span>
    </div>
  );
};

const BullionTile = ({ value, isSelected }: { value: number, isSelected?: boolean }) => {
  return (
    <div className={cn(
      "w-full h-full relative rounded-lg overflow-hidden transition-all duration-300",
      isSelected && "scale-105 z-20"
    )}>
      {/* The Ingot Base */}
      <div className={cn(
        "absolute inset-0 rounded-lg border-b-[3px] transition-colors",
        isSelected ? "bg-amber-400 border-amber-600 shadow-[0_0_12px_rgba(251,191,36,0.6)]" : 
        "bg-amber-500/15 border-amber-500/25"
      )} />

      {/* Number Display */}
      <div className="relative z-10 flex items-center justify-center h-full">
        <span className={cn(
          "font-num font-black text-sm transition-all",
          isSelected ? "text-white" : "text-amber-500/80"
        )}>
          {value}
        </span>
      </div>
    </div>
  );
};

export default function TutorialOverlay({ step, onNext, onReset, onBack, currentScreen, onSkip, onNextMission, user }: TutorialOverlayProps) {
  const { t } = useTranslation();

  // Play success sound when step increases
  useEffect(() => {
    if (step > 1) {
      Promise.resolve().then(() => {
        audioEngine.playSound('success');
      });
    }
  }, [step]);

  const data = TUTORIAL_STEPS[step];
  const translatedTitle = t(`tutorial.step_${step}.title`, { defaultValue: data?.title });
  const translatedMessage = t(`tutorial.step_${step}.message`, { defaultValue: data?.message });
  const [highlightRect, setHighlightRect] = useState<DOMRect | null>(null);
  const [additionalRects, setAdditionalRects] = useState<DOMRect[]>([]);
  const [individualRects, setIndividualRects] = useState<DOMRect[]>([]);
  const [previewTarget, setPreviewTarget] = useState(10);

  useEffect(() => {
    if (step === 16) {
      setPreviewTarget(10);
      const interval = setInterval(() => {
        setPreviewTarget(prev => (prev >= 14 ? 10 : prev + 1));
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [step]);

  // Only show this overlay when we're on the correct screen for this step
  const screenMatch = !data?.screen || data.screen === currentScreen;

  // Update highlight rect whenever step/screen changes, with a retry loop
  // to handle elements that render after a screen transition
  useEffect(() => {
    const hasTarget = data?.highlightId || (data?.highlightIds && data.highlightIds.length > 0);
    if (!hasTarget || !screenMatch) {
      setHighlightRect(null);
      setAdditionalRects([]);
      setIndividualRects([]);
      return;
    }

    let pollInterval: NodeJS.Timeout;
    let pollAttempts = 0;

    // Wait a brief moment before starting to poll, allowing the DOM to render
    const start = setTimeout(() => {
      pollInterval = setInterval(() => {
        let minLeft = Infinity, minTop = Infinity, maxRight = -Infinity, maxBottom = -Infinity;
        let foundAny = false;

        const idsToFind = data.highlightIds || [data.highlightId!];
        const nextIndividualRects: DOMRect[] = [];

        for (const id of idsToFind) {
          const el = document.getElementById(id);
          if (el) {
            const raw = el.getBoundingClientRect();
            // Round coordinates to prevent subpixel jitter that triggers constant SVG repaints
            const r = new DOMRect(Math.round(raw.left), Math.round(raw.top), Math.round(raw.width), Math.round(raw.height));
            nextIndividualRects.push(r);
            if (r.left < minLeft) minLeft = r.left;
            if (r.top < minTop) minTop = r.top;
            if (r.right > maxRight) maxRight = r.right;
            if (r.bottom > maxBottom) maxBottom = r.bottom;
            foundAny = true;
          }
        }

        if (foundAny) {
          setHighlightRect(prev => {
            if (!prev || prev.top !== minTop || prev.left !== minLeft || prev.right !== maxRight || prev.bottom !== maxBottom) {
              return new DOMRect(minLeft, minTop, maxRight - minLeft, maxBottom - minTop);
            }
            return prev;
          });
          setIndividualRects(prev => {
            if (prev.length !== nextIndividualRects.length) return nextIndividualRects;
            const changed = prev.some((r, i) => {
              const nr = nextIndividualRects[i];
              return r.top !== nr.top || r.left !== nr.left || r.right !== nr.right || r.bottom !== nr.bottom;
            });
            return changed ? nextIndividualRects : prev;
          });
        }

        // Measure additional highlight elements
        const additionalIds = data.additionalHighlightIds || [];
        const nextAddRects: DOMRect[] = [];
        for (const id of additionalIds) {
          const el = document.getElementById(id);
          if (el) {
            const raw = el.getBoundingClientRect();
            nextAddRects.push(new DOMRect(Math.round(raw.left), Math.round(raw.top), Math.round(raw.width), Math.round(raw.height)));
          }
        }
        setAdditionalRects(prev => {
          if (prev.length !== nextAddRects.length) return nextAddRects;
          const changed = prev.some((r, i) => {
            const nr = nextAddRects[i];
            return r.top !== nr.top || r.left !== nr.left || r.right !== nr.right || r.bottom !== nr.bottom;
          });
          return changed ? nextAddRects : prev;
        });

        // Stop polling after ~1.5 seconds (plenty of time for animations to settle)
        if (pollAttempts++ > 30) {
          clearInterval(pollInterval);
        }
      }, 50);
    }, 50);

    return () => {
      clearTimeout(start);
      clearInterval(pollInterval);
    };
  }, [step, data?.highlightId, data?.highlightIds, data?.additionalHighlightIds, currentScreen, screenMatch]);

  // Re-measure on window resize
  useEffect(() => {
    const hasTarget = data?.highlightId || (data?.highlightIds && data.highlightIds.length > 0);
    if (!hasTarget) return;
    const onResize = () => {
      let minLeft = Infinity, minTop = Infinity, maxRight = -Infinity, maxBottom = -Infinity;
      let foundAny = false;
      const idsToFind = data.highlightIds || [data.highlightId!];
      const nextIndividualRects: DOMRect[] = [];
      
      for (const id of idsToFind) {
        const el = document.getElementById(id);
        if (el) {
          const r = el.getBoundingClientRect();
          nextIndividualRects.push(r);
          if (r.left < minLeft) minLeft = r.left;
          if (r.top < minTop) minTop = r.top;
          if (r.right > maxRight) maxRight = r.right;
          if (r.bottom > maxBottom) maxBottom = r.bottom;
          foundAny = true;
        }
      }
      if (foundAny) {
        setHighlightRect(new DOMRect(minLeft, minTop, maxRight - minLeft, maxBottom - minTop));
        setIndividualRects(nextIndividualRects);
      }

      // Measure additional highlight elements
      const additionalIds = data.additionalHighlightIds || [];
      const nextAddRects: DOMRect[] = [];
      for (const id of additionalIds) {
        const el = document.getElementById(id);
        if (el) {
          nextAddRects.push(el.getBoundingClientRect());
        }
      }
      setAdditionalRects(nextAddRects);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [data?.highlightId, data?.highlightIds, data?.additionalHighlightIds]);

  // Passthrough interaction listener — advances step when user completes an action in the highlight zone
  useEffect(() => {
    if (!data || !highlightRect) return;

    if (data.action === 'click') {
      const handleClick = (e: MouseEvent) => {
        if ([33, 39, 41, 42, 44, 45, 46, 48, 49].includes(step)) return; // Wait for custom handlers instead
        const { clientX, clientY } = e;
        if (
          clientX >= highlightRect.left - PAD &&
          clientX <= highlightRect.right + PAD &&
          clientY >= highlightRect.top - PAD &&
          clientY <= highlightRect.bottom + PAD
        ) {
          setTimeout(onNext, 50);
        }
      };
      window.addEventListener('click', handleClick, true);
      return () => window.removeEventListener('click', handleClick, true);
    } 
    
    if (data.action === 'drag' || data.action === 'multi-click') {
      // Game components handle validation and call onNextTutorialStep/onNext themselves.
      return;
    }
  }, [step, highlightRect, onNext, data]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    // No-op. User must click the CONTINUE button or complete the highlighted action.
  };

  if (!data) return null;
  if (!screenMatch) {
    return (
      <div className="fixed inset-0 z-[9999] bg-slate-950/95 pointer-events-none transition-colors duration-300" />
    );
  }

  // Padded highlight bounds (used for the glow ring and SVG cutout)
  const hl = (highlightRect && screenMatch)
    ? {
        left: highlightRect.left - PAD,
        top: highlightRect.top - PAD,
        right: highlightRect.right + PAD,
        bottom: highlightRect.bottom + PAD,
        width: highlightRect.width + PAD * 2,
        height: highlightRect.height + PAD * 2,
        borderRadius: Math.min(20, (highlightRect.height + PAD * 2) / 2),
      }
    : null;

  const hls = [];
  if (hl) {
    hls.push(hl);
  }
  if (screenMatch) {
    for (const r of additionalRects) {
      hls.push({
        left: r.left - PAD,
        top: r.top - PAD,
        right: r.right + PAD,
        bottom: r.bottom + PAD,
        width: r.width + PAD * 2,
        height: r.height + PAD * 2,
        borderRadius: Math.min(20, (r.height + PAD * 2) / 2),
      });
    }
  }

  const W = window.innerWidth;
  const H = window.innerHeight;

  // SVG path: full screen rect minus rounded-rect cutouts for all highlights
  let svgMask = `M0,0 H${W} V${H} H0 Z`;
  if (hls.length > 0) {
    for (const item of hls) {
      svgMask += ` M${item.left + item.borderRadius},${item.top} ` +
        `Q${item.left},${item.top} ${item.left},${item.top + item.borderRadius} ` +
        `V${item.bottom - item.borderRadius} Q${item.left},${item.bottom} ${item.left + item.borderRadius},${item.bottom} ` +
        `H${item.right - item.borderRadius} Q${item.right},${item.bottom} ${item.right},${item.bottom - item.borderRadius} ` +
        `V${item.top + item.borderRadius} Q${item.right},${item.top} ${item.right - item.borderRadius},${item.top} Z`;
    }
  }

  // Dialog placement: below highlight if highlight is in top half, else above
  const dialogBelow = data.dialogPosition 
    ? (data.dialogPosition === 'bottom')
    : (hl ? hl.top < H * 0.5 : false);

  const renderTitle = (title: string, screen?: string) => {
    if (!title.toLowerCase().includes('fever')) return title;
    
    let gameMode = title.replace(/fever\s*mode|fever/i, '').trim();
    if (screen === 'sumgame') gameMode = 'Shell Crush';
    else if (screen === 'goldrush') gameMode = 'Gold Rush';
    else if (screen === 'game') gameMode = 'Cloud Mach';
    else if (screen === 'applegame') gameMode = 'Star Burst';
    
    return (
      <div className="flex flex-col items-start">
        {gameMode && <span>{gameMode}</span>}
        <span className="text-rose-500 text-lg mt-1">Fever Mode</span>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[9999] pointer-events-none">

      {/* ── Dark backdrop with cutout (SVG so we get rounded corners) ── */}
      {/* The SVG itself must be pointer-events-none so it doesn't block the hole */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{ transform: 'translateZ(0)', willChange: 'opacity, d' }}
      >
        <path 
          d={svgMask} 
          fill={(data.action === 'none' && !(step === 36 && user?.nickname !== null)) ? "rgba(10,15,30,0)" : "rgba(10,15,30,0.72)"} 
          fillRule="evenodd" 
        />
      </svg>

      {/* ── Invisible Click Catchers ── */}
      {/* We use 4 divs framing the hole to catch backdrop clicks while letting the hole pass events through */}
      {(data.action !== 'none' || (step === 36 && user?.nickname !== null)) && (
        hl ? (
          <>
            <div className="absolute top-0 left-0 right-0 pointer-events-auto" style={{ height: hl.top }} onClick={handleBackdropClick} />
            <div className="absolute bottom-0 left-0 right-0 pointer-events-auto" style={{ top: hl.bottom }} onClick={handleBackdropClick} />
            <div className="absolute pointer-events-auto" style={{ top: hl.top, height: hl.height, left: 0, width: hl.left }} onClick={handleBackdropClick} />
            <div className="absolute pointer-events-auto" style={{ top: hl.top, height: hl.height, left: hl.right, right: 0 }} onClick={handleBackdropClick} />
          </>
        ) : (
          <div className="absolute inset-0 pointer-events-auto" onClick={handleBackdropClick} />
        )
      )}

      {/* ── Soft glowing ring around the highlighted elements ── */}
      <AnimatePresence>
        {hls.map((item, idx) => (
          <motion.div
            key={`ring-${step}-${idx}`}
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="absolute pointer-events-none"
            style={{
              left: item.left,
              top: item.top,
              width: item.width,
              height: item.height,
              borderRadius: item.borderRadius,
              border: '2px solid rgba(251,191,36,0.9)',
              boxShadow: '0 0 0 4px rgba(251,191,36,0.18), 0 0 24px 6px rgba(251,191,36,0.22)',
            }}
          />
        ))}
      </AnimatePresence>

      {/* ── Pulsing tap dot (click steps) ── */}
      <AnimatePresence>
        {hl && data.action === 'click' && (
          <motion.div
            key={`dot-${step}`}
            className="absolute pointer-events-none"
            style={{
              left: hl.left + hl.width / 2 - 6,
              top: hl.top + hl.height / 2 - 6,
            }}
            animate={{ scale: [0.9, 1.25, 0.9], opacity: [0.8, 1, 0.8] }}
            transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
          >
            <div style={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              background: 'rgba(251,191,36,1)',
              boxShadow: '0 0 10px 4px rgba(251,191,36,0.6)',
            }} />
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {hl && data.action === 'drag' && (() => {
          if (individualRects.length >= 2) {
            // Draw connecting path SVG
            const points = individualRects.map(r => ({
              x: r.left + r.width / 2,
              y: r.top + r.height / 2
            }));

            // Construct SVG path "d" string
            const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

            // Calculate moving hand keypoints
            const pathX = points.map(p => p.x - 16); 
            const pathY = points.map(p => p.y - 16);

            return (
              <div key={`drag-path-${step}`} className="absolute inset-0 pointer-events-none z-30">
                <style>{`
                  @keyframes tutorialDash {
                    to {
                      stroke-dashoffset: -40;
                    }
                  }
                `}</style>
                <svg className="absolute inset-0 w-full h-full pointer-events-none">
                  {/* Glowing/Dashed Drag Path line */}
                  <path 
                    d={pathD} 
                    stroke="rgba(34, 211, 238, 0.45)" 
                    strokeWidth={8}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeDasharray="12 8"
                    fill="none"
                    style={{ animation: 'tutorialDash 1.2s linear infinite' }}
                  />
                  {/* Thin center core */}
                  <path 
                    d={pathD} 
                    stroke="rgba(34, 211, 238, 0.7)" 
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                </svg>

                {/* Looping Pointer Finger */}
                <motion.div
                  className="absolute"
                  style={{ width: 32, height: 32 }}
                  animate={{
                    x: pathX,
                    y: pathY,
                    opacity: [0, 1, 1, 0]
                  }}
                  transition={{
                    duration: 1.8,
                    repeat: Infinity,
                    ease: "easeInOut",
                    times: [0, 0.1, 0.9, 1]
                  }}
                >
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="8" cy="8" r="8" fill="rgba(34, 211, 238, 0.4)" className="animate-ping" />
                    <circle cx="8" cy="8" r="4" fill="rgba(34, 211, 238, 0.8)" />
                    <g transform="translate(4, 4)">
                      <path d="M12.8 19.2C12.8 19.2 12.8 17.6 11.2 16C9.6 14.4 8 14.4 8 14.4V4.8C8 3.03 6.97 2 5.6 2C4.23 2 3.2 3.03 3.2 4.8V16.8L2.4 16C1.6 15.2 0.8 14.4 0 15.2L4 23.2C4.8 24.8 6.4 25.6 8 25.6H13.6C15.2 25.6 16.8 24 16.8 22.4L16 19.2H12.8Z" fill="#FBBF24" stroke="white" strokeWidth="1.5" strokeLinejoin="round" />
                    </g>
                  </svg>
                </motion.div>
              </div>
            );
          }

          // Fallback to legacy straight sweep
          const isVertical = hl.height > hl.width;
          const sweepDist = isVertical ? 48 : 48;
          
          return (
            <motion.div
              key={`drag-${step}`}
              className="absolute pointer-events-none"
              style={{
                left: isVertical ? hl.left + hl.width / 2 - 12 : hl.left + hl.width * 0.1,
                top: isVertical ? hl.top + hl.height * 0.1 : hl.top + hl.height / 2 - 12,
                width: 24,
                height: 24,
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className="relative w-full h-full"
                animate={isVertical ? { y: [0, sweepDist, 0] } : { x: [0, sweepDist, 0] }}
                transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
              >
                <div className="absolute inset-0 z-10" style={{
                  borderRadius: '50%',
                  background: 'rgba(251,191,36,0.95)',
                  border: '2px solid white',
                  boxShadow: '0 0 12px 4px rgba(251,191,36,0.6)',
                }} />
                
                <motion.div
                  className="absolute"
                  animate={isVertical ? { scaleY: [0.4, 1, 0.4], opacity: [0.4, 1, 0.4] } : { scaleX: [0.4, 1, 0.4], opacity: [0.4, 1, 0.4] }}
                  transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
                  style={isVertical ? {
                    width: 6,
                    height: 40,
                    borderRadius: 3,
                    background: 'linear-gradient(180deg, rgba(251,191,36,0.8), rgba(251,191,36,0))',
                    transformOrigin: 'top',
                    top: '100%',
                    left: '50%',
                    marginLeft: -3,
                    marginTop: 4,
                  } : {
                    width: 40,
                    height: 6,
                    borderRadius: 3,
                    background: 'linear-gradient(90deg, rgba(251,191,36,0.8), rgba(251,191,36,0))',
                    transformOrigin: 'left',
                    left: '100%',
                    top: '50%',
                    marginTop: -3,
                    marginLeft: 4,
                  }}
                />
              </motion.div>
            </motion.div>
          );
        })()}
      </AnimatePresence>

      {/* ── Dialog card ── */}
      <div className={cn(
        "absolute inset-0 flex justify-center pointer-events-none",
        step === 1 ? "items-center" : "items-start"
      )}>
        <div className={cn("w-full max-w-md relative flex justify-center", step === 1 && "h-full items-center")}>
          <AnimatePresence mode="wait">
            {screenMatch && (
              <>
                <motion.div
                  key={`text-${step}`}
                  initial={{ opacity: 0, y: -16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.22 }}
                  className={cn(
                    "bg-white rounded-3xl shadow-2xl border-b-4 border-slate-100 pointer-events-auto",
                    step === 1 ? "relative w-[calc(100%-2.5rem)] p-8 text-center" : "absolute left-5 right-5 p-6",
                    step !== 1 && (dialogBelow ? "top-28" : "top-6")
                  )}
                >
                <div className="flex justify-between items-center mb-2">
                  <div className="text-xs font-black text-slate-400 uppercase tracking-widest leading-none">
                    Step {step} of {Object.keys(TUTORIAL_STEPS).length}
                  </div>
                </div>
                <div className={cn("flex items-center gap-2 mb-3", step === 1 && "justify-center")}>
                  <h3 className="text-2xl font-num text-[#0ea5e9] leading-none">{renderTitle(translatedTitle, data.screen)}</h3>
                </div>
                {translatedMessage && (
                  <p className={cn(
                    "text-slate-700 text-lg font-medium leading-relaxed whitespace-pre-line",
                    (data.action === 'any' || 
                     data.action === 'drag' || 
                     ((data.action === 'click' || data.action === 'multi-click') && step !== 20) || 
                     [7, 16, 23, 24].includes(step)) 
                      ? "mb-6" 
                      : "mb-0"
                  )}>
                    {translatedMessage}
                  </p>
                )}

                {data.action === 'any' && (
                  <button
                    onClick={onNext}
                    className="w-full bg-[#fbbf24] text-slate-900 font-black py-4 rounded-2xl toy-3d-button border-none active:scale-95 transition-all text-xl mt-6"
                  >
                    CONTINUE
                  </button>
                )}

                {step === 7 && (
                  <div className="flex flex-col gap-3 my-5 p-4 bg-slate-50 rounded-2xl border border-slate-100/80">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">Example Combinations (Sum = 10)</span>
                    <div className="flex flex-wrap gap-2.5 items-center">
                      {/* Example 1: 3-4-3 */}
                      <div className="flex items-center gap-1 bg-[#fef3c7] px-3 py-2 rounded-2xl shadow-[inset_0_0_8px_rgba(255,255,255,0.8)] border-b-2 border-[#f59e0b] border-t-0 border-x-0">
                        <div className="w-7 h-7 flex-shrink-0"><ShellTile value={3} /></div>
                        <span className="text-[#f59e0b] font-black text-[11px] px-1 font-num">→</span>
                        <div className="w-7 h-7 flex-shrink-0"><ShellTile value={4} /></div>
                        <span className="text-[#f59e0b] font-black text-[11px] px-1 font-num">→</span>
                        <div className="w-7 h-7 flex-shrink-0"><ShellTile value={3} /></div>
                      </div>

                      {/* Example 2: 5-5 */}
                      <div className="flex items-center gap-1 bg-[#fef3c7] px-3 py-2 rounded-2xl shadow-[inset_0_0_8px_rgba(255,255,255,0.8)] border-b-2 border-[#f59e0b] border-t-0 border-x-0">
                        <div className="w-7 h-7 flex-shrink-0"><ShellTile value={5} /></div>
                        <span className="text-[#f59e0b] font-black text-[11px] px-1 font-num">→</span>
                        <div className="w-7 h-7 flex-shrink-0"><ShellTile value={5} /></div>
                      </div>

                      {/* Example 3: 1-9 */}
                      <div className="flex items-center gap-1 bg-[#fef3c7] px-3 py-2 rounded-2xl shadow-[inset_0_0_8px_rgba(255,255,255,0.8)] border-b-2 border-[#f59e0b] border-t-0 border-x-0">
                        <div className="w-7 h-7 flex-shrink-0"><ShellTile value={1} /></div>
                        <span className="text-[#f59e0b] font-black text-[11px] px-1 font-num">→</span>
                        <div className="w-7 h-7 flex-shrink-0"><ShellTile value={9} isBlue={true} /></div>
                      </div>
                    </div>
                  </div>
                )}

                {step === 9 && (
                  <div className="flex justify-center my-6">
                    {/* Animated 3x3 Grid Panel */}
                    <div className="relative flex-shrink-0 w-32 h-32 bg-[#fef3c7] rounded-[32px] p-2.5 border-b-[6px] border-[#f59e0b] shadow-[0_8px_20px_rgba(0,0,0,0.06),inset_0_0_12px_rgba(255,255,255,0.85)] flex items-center justify-center overflow-hidden ring-4 ring-indigo-50/50">
                      {/* Explosion Blast Ring */}
                      <motion.div 
                        className="absolute w-8 h-8 rounded-full border-4 border-amber-400 bg-amber-300/30 z-20 pointer-events-none"
                        animate={{
                          scale: [0.2, 0.2, 3, 3, 0.2],
                          opacity: [0, 0, 0.9, 0, 0]
                        }}
                        transition={{
                          duration: 3.2,
                          repeat: Infinity,
                          times: [0, 0.44, 0.54, 0.64, 1],
                          ease: "easeOut"
                        }}
                      />
                      
                      {/* Particle Sparks on Explosion (Cross Shape) */}
                      <motion.div 
                        className="absolute inset-0 z-30 pointer-events-none flex items-center justify-center"
                        animate={{
                          opacity: [0, 0, 1, 0, 0]
                        }}
                        transition={{
                          duration: 3.2,
                          repeat: Infinity,
                          times: [0, 0.44, 0.48, 0.62, 1]
                        }}
                      >
                        {/* 4 cross spark elements */}
                        <div className="absolute w-6 h-1.5 bg-amber-400 rounded-full translate-x-7" /> {/* Right */}
                        <div className="absolute w-6 h-1.5 bg-amber-400 rounded-full -translate-x-7" /> {/* Left */}
                        <div className="absolute w-1.5 h-6 bg-amber-400 rounded-full translate-y-7" /> {/* Down */}
                        <div className="absolute w-1.5 h-6 bg-amber-400 rounded-full -translate-y-7" /> {/* Up */}
                      </motion.div>

                      {/* 3x3 Grid Layout */}
                      <div className="grid grid-cols-3 gap-1.5 relative z-10">
                        {/* Row 0 */}
                        <div className="w-8 h-8">
                          <ShellTile value={4} />
                        </div>
                        <motion.div 
                          className="w-8 h-8" 
                          animate={{ scale: [1, 1, 0, 0, 1], opacity: [1, 1, 0, 0, 1] }} 
                          transition={{ duration: 3.2, repeat: Infinity, times: [0, 0.44, 0.54, 0.8, 1] }}
                        >
                          <ShellTile value={2} />
                        </motion.div>
                        <div className="w-8 h-8">
                          <ShellTile value={3} />
                        </div>

                        {/* Row 1 */}
                        <motion.div 
                          className="w-8 h-8" 
                          animate={{ scale: [1, 1, 0, 0, 1], opacity: [1, 1, 0, 0, 1] }} 
                          transition={{ duration: 3.2, repeat: Infinity, times: [0, 0.44, 0.54, 0.8, 1] }}
                        >
                          <ShellTile value={1} />
                        </motion.div>
                        {/* Center 9 Shell */}
                        <motion.div 
                          className="w-8 h-8 z-10" 
                          animate={{ 
                            scale: [1, 1.25, 0, 0, 1],
                            opacity: [1, 1, 0, 0, 1],
                            rotate: [0, 15, 360, 360, 0] 
                          }} 
                          transition={{ 
                            duration: 3.2, 
                            repeat: Infinity, 
                            times: [0, 0.26, 0.30, 0.8, 1],
                            ease: "easeInOut"
                          }}
                        >
                          <ShellTile value={9} isBlue={true} />
                        </motion.div>
                        <motion.div 
                          className="w-8 h-8" 
                          animate={{ scale: [1, 1, 0, 0, 1], opacity: [1, 1, 0, 0, 1] }} 
                          transition={{ duration: 3.2, repeat: Infinity, times: [0, 0.44, 0.54, 0.8, 1] }}
                        >
                          <ShellTile value={5} />
                        </motion.div>

                        {/* Row 2 */}
                        <div className="w-8 h-8">
                          <ShellTile value={3} />
                        </div>
                        <motion.div 
                          className="w-8 h-8" 
                          animate={{ scale: [1, 1, 0, 0, 1], opacity: [1, 1, 0, 0, 1] }} 
                          transition={{ duration: 3.2, repeat: Infinity, times: [0, 0.44, 0.54, 0.8, 1] }}
                        >
                          <ShellTile value={6} />
                        </motion.div>
                        <div className="w-8 h-8">
                          <ShellTile value={2} />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {step === 11 && (
                  <div className="flex justify-center my-6">
                    {/* Animated 3x3 Grid Panel */}
                    <div className="relative flex-shrink-0 w-32 h-32 bg-[#fef3c7] rounded-[32px] p-2.5 border-b-[6px] border-[#f59e0b] shadow-[0_8px_20px_rgba(0,0,0,0.06),inset_0_0_12px_rgba(255,255,255,0.85)] flex items-center justify-center overflow-hidden ring-4 ring-indigo-50/50">
                      {/* Explosion Blast Ring (Expanding Square) */}
                      <motion.div 
                        className="absolute w-8 h-8 rounded-[16px] border-4 border-amber-400 bg-amber-300/30 z-20 pointer-events-none"
                        animate={{
                          scale: [0.2, 0.2, 3, 3, 0.2],
                          opacity: [0, 0, 0.9, 0, 0]
                        }}
                        transition={{
                          duration: 3.2,
                          repeat: Infinity,
                          times: [0, 0.44, 0.54, 0.64, 1],
                          ease: "easeOut"
                        }}
                      />
                      
                      {/* Connecting Square Outline (around the red center) */}
                      <motion.div 
                        className="absolute w-[78px] h-[78px] rounded-[16px] border-[3px] border-amber-400 shadow-[0_0_12px_rgba(245,158,11,0.8),inset_0_0_8px_rgba(245,158,11,0.4)] z-20 pointer-events-none"
                        animate={{
                          opacity: [0, 0, 1, 0, 0]
                        }}
                        transition={{
                          duration: 3.2,
                          repeat: Infinity,
                          times: [0, 0.44, 0.48, 0.62, 1]
                        }}
                      />

                      {/* 3x3 Grid Layout */}
                      <div className="grid grid-cols-3 gap-1.5 relative z-10">
                        {/* Row 0 */}
                        <motion.div 
                          className="w-8 h-8" 
                          animate={{ scale: [1, 1, 0, 0, 1], opacity: [1, 1, 0, 0, 1] }} 
                          transition={{ duration: 3.2, repeat: Infinity, times: [0, 0.44, 0.54, 0.8, 1] }}
                        >
                          <ShellTile value={4} />
                        </motion.div>
                        <motion.div 
                          className="w-8 h-8" 
                          animate={{ scale: [1, 1, 0, 0, 1], opacity: [1, 1, 0, 0, 1] }} 
                          transition={{ duration: 3.2, repeat: Infinity, times: [0, 0.44, 0.54, 0.8, 1] }}
                        >
                          <ShellTile value={2} />
                        </motion.div>
                        <motion.div 
                          className="w-8 h-8" 
                          animate={{ scale: [1, 1, 0, 0, 1], opacity: [1, 1, 0, 0, 1] }} 
                          transition={{ duration: 3.2, repeat: Infinity, times: [0, 0.44, 0.54, 0.8, 1] }}
                        >
                          <ShellTile value={3} />
                        </motion.div>

                        {/* Row 1 */}
                        <motion.div 
                          className="w-8 h-8" 
                          animate={{ scale: [1, 1, 0, 0, 1], opacity: [1, 1, 0, 0, 1] }} 
                          transition={{ duration: 3.2, repeat: Infinity, times: [0, 0.44, 0.54, 0.8, 1] }}
                        >
                          <ShellTile value={1} />
                        </motion.div>
                        {/* Center 9 Shell */}
                        <motion.div 
                          className="w-8 h-8 z-10" 
                          animate={{ 
                            scale: [1, 1.25, 0, 0, 1],
                            opacity: [1, 1, 0, 0, 1],
                            rotate: [0, 15, 360, 360, 0] 
                          }} 
                          transition={{ 
                            duration: 3.2, 
                            repeat: Infinity, 
                            times: [0, 0.26, 0.30, 0.8, 1],
                            ease: "easeInOut"
                          }}
                        >
                          <ShellTile value={9} isFever={true} />
                        </motion.div>
                        <motion.div 
                          className="w-8 h-8" 
                          animate={{ scale: [1, 1, 0, 0, 1], opacity: [1, 1, 0, 0, 1] }} 
                          transition={{ duration: 3.2, repeat: Infinity, times: [0, 0.44, 0.54, 0.8, 1] }}
                        >
                          <ShellTile value={5} />
                        </motion.div>

                        {/* Row 2 */}
                        <motion.div 
                          className="w-8 h-8" 
                          animate={{ scale: [1, 1, 0, 0, 1], opacity: [1, 1, 0, 0, 1] }} 
                          transition={{ duration: 3.2, repeat: Infinity, times: [0, 0.44, 0.54, 0.8, 1] }}
                        >
                          <ShellTile value={3} />
                        </motion.div>
                        <motion.div 
                          className="w-8 h-8" 
                          animate={{ scale: [1, 1, 0, 0, 1], opacity: [1, 1, 0, 0, 1] }} 
                          transition={{ duration: 3.2, repeat: Infinity, times: [0, 0.44, 0.54, 0.8, 1] }}
                        >
                          <ShellTile value={6} />
                        </motion.div>
                        <motion.div 
                          className="w-8 h-8" 
                          animate={{ scale: [1, 1, 0, 0, 1], opacity: [1, 1, 0, 0, 1] }} 
                          transition={{ duration: 3.2, repeat: Infinity, times: [0, 0.44, 0.54, 0.8, 1] }}
                        >
                          <ShellTile value={2} />
                        </motion.div>
                      </div>
                    </div>
                  </div>
                )}

                {step === 15 && (
                  <div className="flex flex-col items-center justify-center my-6 gap-3">
                    {/* Sum HUD target visualizer replicating the top bar exactly */}
                    <div className="flex flex-col items-center bg-slate-950/50 backdrop-blur-md px-6 py-3 rounded-2xl border border-white/10 shadow-2xl min-w-[110px]">
                      <span className="text-[9px] font-black text-amber-500 tracking-[0.25em] uppercase mb-1.5 leading-none">TARGET</span>
                      <motion.span 
                        className="text-3xl font-num font-black text-white leading-none drop-shadow-[0_2px_8px_rgba(251,191,36,0.4)]"
                        animate={{ scale: [1, 1, 1.15, 1, 1] }}
                        transition={{ duration: 3.2, repeat: Infinity, times: [0, 0.56, 0.60, 0.81, 1] }}
                      >
                        10
                      </motion.span>
                    </div>

                    {/* Animated Gold Ingot Drag Box (Compact & Narrower) */}
                    <div className="relative w-36 h-16 bg-[#2d3748]/50 rounded-2xl p-2 border border-white/5 shadow-inner flex items-center justify-center overflow-hidden">
                      {/* Connection path line */}
                      <div className="absolute left-[32%] right-[32%] h-[3px] bg-white/10 rounded" />
                      <motion.div 
                        className="absolute left-[32%] h-[3px] bg-amber-400 rounded shadow-[0_0_8px_rgba(251,191,36,0.8)]"
                        style={{ originX: 0 }}
                        animate={{
                          width: ["0%", "0%", "36%", "36%", "0%"]
                        }}
                        transition={{
                          duration: 3.2,
                          repeat: Infinity,
                          times: [0, 0.25, 0.56, 0.81, 1],
                          ease: "easeInOut"
                        }}
                      />

                      {/* Side by side Tiles */}
                      <div className="flex justify-between w-full px-2 z-10">
                        {/* Bullion 6 */}
                        <motion.div 
                          className="w-9 h-9"
                          animate={{
                            scale: [1, 1, 1.05, 1.05, 1, 1]
                          }}
                          transition={{
                            duration: 3.2,
                            repeat: Infinity,
                            times: [0, 0.25, 0.26, 0.81, 0.82, 1]
                          }}
                        >
                          <div className="w-full h-full relative">
                            <motion.div 
                              className="absolute inset-0 rounded-lg border-b-[2px] transition-colors border-white/5 bg-white/10"
                              animate={{
                                backgroundColor: ["rgba(255,255,255,0.1)", "rgba(255,255,255,0.1)", "rgba(251,191,36,1)", "rgba(251,191,36,1)", "rgba(255,255,255,0.1)"],
                                borderColor: ["rgba(255,255,255,0.05)", "rgba(255,255,255,0.05)", "rgba(217,119,6,1)", "rgba(217,119,6,1)", "rgba(255,255,255,0.05)"],
                                boxShadow: ["none", "none", "0 0 8px rgba(251,191,36,0.6)", "0 0 8px rgba(251,191,36,0.6)", "none"]
                              }}
                              transition={{
                                duration: 3.2,
                                repeat: Infinity,
                                times: [0, 0.25, 0.26, 0.81, 0.82, 1]
                              }}
                            />
                            <div className="relative z-10 flex items-center justify-center h-full">
                              <motion.span 
                                className="font-num font-black text-xs text-amber-500/80"
                                animate={{
                                  color: ["#f59e0b", "#f59e0b", "#ffffff", "#ffffff", "#f59e0b"]
                                }}
                                transition={{
                                  duration: 3.2,
                                  repeat: Infinity,
                                  times: [0, 0.25, 0.26, 0.81, 0.82, 1]
                                }}
                              >
                                6
                              </motion.span>
                            </div>
                          </div>
                        </motion.div>

                        {/* Bullion 4 */}
                        <motion.div 
                          className="w-9 h-9"
                          animate={{
                            scale: [1, 1, 1.05, 1.05, 1, 1]
                          }}
                          transition={{
                            duration: 3.2,
                            repeat: Infinity,
                            times: [0, 0.55, 0.56, 0.81, 0.82, 1]
                          }}
                        >
                          <div className="w-full h-full relative">
                            <motion.div 
                              className="absolute inset-0 rounded-lg border-b-[2px] transition-colors border-white/5 bg-white/10"
                              animate={{
                                backgroundColor: ["rgba(255,255,255,0.1)", "rgba(255,255,255,0.1)", "rgba(251,191,36,1)", "rgba(251,191,36,1)", "rgba(255,255,255,0.1)"],
                                borderColor: ["rgba(255,255,255,0.05)", "rgba(255,255,255,0.05)", "rgba(217,119,6,1)", "rgba(217,119,6,1)", "rgba(255,255,255,0.05)"],
                                boxShadow: ["none", "none", "0 0 8px rgba(251,191,36,0.6)", "0 0 8px rgba(251,191,36,0.6)", "none"]
                              }}
                              transition={{
                                duration: 3.2,
                                repeat: Infinity,
                                times: [0, 0.55, 0.56, 0.81, 0.82, 1]
                              }}
                            />
                            <div className="relative z-10 flex items-center justify-center h-full">
                              <motion.span 
                                className="font-num font-black text-xs text-amber-500/80"
                                animate={{
                                  color: ["#f59e0b", "#f59e0b", "#ffffff", "#ffffff", "#f59e0b"]
                                }}
                                transition={{
                                  duration: 3.2,
                                  repeat: Infinity,
                                  times: [0, 0.55, 0.56, 0.81, 0.82, 1]
                                }}
                              >
                                4
                              </motion.span>
                            </div>
                          </div>
                        </motion.div>
                      </div>

                      {/* Animated Dragging Pointer (Finger/Hand) */}
                      <motion.div 
                        className="absolute w-5 h-5 rounded-full border border-amber-400 bg-amber-400/40 z-30 pointer-events-none flex items-center justify-center"
                        animate={{
                          x: [-36, -36, 36, 36, -36],
                          opacity: [0, 1, 1, 0, 0]
                        }}
                        transition={{
                          duration: 3.2,
                          repeat: Infinity,
                          times: [0, 0.22, 0.56, 0.81, 1],
                          ease: "easeInOut"
                        }}
                      >
                        <div className="w-2 h-2 rounded-full bg-amber-400" />
                      </motion.div>

                      {/* Success Checkmark HUD popping up when sum = 10 */}
                      <motion.div
                        className="absolute inset-0 bg-[#fbbf24] z-20 pointer-events-none flex flex-col items-center justify-center"
                        animate={{
                          opacity: [0, 0, 0.98, 0.98, 0],
                          scale: [0.8, 0.8, 1, 1, 0.8]
                        }}
                        transition={{
                          duration: 3.2,
                          repeat: Infinity,
                          times: [0, 0.56, 0.60, 0.81, 1],
                          ease: "easeOut"
                        }}
                      >
                        <span className="text-slate-950 font-black text-[9px] uppercase tracking-widest mb-0.5">MATCHED!</span>
                        <div className="flex items-center gap-1">
                          <span className="font-num font-black text-slate-900 text-xs">6 + 4 =</span>
                          <span className="font-num font-black bg-slate-950 text-amber-400 px-1.5 py-0.5 rounded text-[10px] shadow-md">10</span>
                        </div>
                      </motion.div>
                    </div>
                  </div>
                )}

                {step === 16 && (
                  <div className="flex flex-col items-center justify-center my-6 gap-5">
                    {/* The Incrementing Target HUD pill replicating the user's image exactly */}
                    <div className="relative flex items-center justify-center h-20 w-full">
                      {/* Shockwave circle behind */}
                      <AnimatePresence>
                        {previewTarget > 10 && (
                          <motion.div 
                            key={`shock-${previewTarget}`}
                            className="absolute w-10 h-10 rounded-full border border-amber-400 bg-amber-400/20 z-0 pointer-events-none"
                            initial={{ scale: 0.5, opacity: 0 }}
                            animate={{ scale: [0.5, 2.5], opacity: [0, 0.8, 0] }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.6, ease: "easeOut" }}
                          />
                        )}
                      </AnimatePresence>

                      {/* Float-up "+1" text */}
                      <AnimatePresence>
                        {previewTarget > 10 && (
                          <motion.span 
                            key={`plus1-${previewTarget}`}
                            className="absolute font-num font-black text-base text-emerald-400 drop-shadow-[0_2px_8px_rgba(52,211,153,0.6)] z-30"
                            style={{ right: "24%", top: "8px" }}
                            initial={{ y: 8, opacity: 0 }}
                            animate={{ y: -24, opacity: [0, 1, 1, 0] }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                          >
                            +1
                          </motion.span>
                        )}
                      </AnimatePresence>

                      {/* The Stacked Target Box */}
                      <motion.div 
                        key={`pill-${previewTarget}`}
                        className="relative flex flex-col items-center bg-slate-950/50 backdrop-blur-md px-6 py-3 rounded-2xl border border-white/10 shadow-2xl min-w-[110px] z-10"
                        animate={{
                          scale: [1, 1.15, 1]
                        }}
                        transition={{
                          duration: 0.4,
                          ease: "easeInOut"
                        }}
                      >
                        <span className="text-[9px] font-black text-amber-500 tracking-[0.25em] uppercase mb-1.5 leading-none">TARGET</span>
                        
                        {/* Rounded Target Value Square */}
                        <div className="relative h-8 w-12 flex items-center justify-center overflow-hidden">
                          <AnimatePresence mode="popLayout">
                            <motion.span 
                              key={previewTarget}
                              className="absolute font-num font-black text-white text-3xl drop-shadow-[0_2px_8px_rgba(251,191,36,0.4)]"
                              initial={{ y: 18, opacity: 0 }}
                              animate={{ y: 0, opacity: 1 }}
                              exit={{ y: -18, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              {previewTarget}
                            </motion.span>
                          </AnimatePresence>
                        </div>
                      </motion.div>
                    </div>

                    {/* Explanatory visual cue grid preview displaying 11, 12, 13 ... */}
                    <div className="flex flex-col gap-2 items-center bg-slate-950/40 px-5 py-3 rounded-2xl border border-white/5 shadow-inner">
                      <span className="text-slate-400 text-xs font-semibold tracking-wide">Target Increases:</span>
                      <div className="flex items-center gap-1 sm:gap-2">
                        {[10, 11, 12, 13, 14].map((val, idx) => (
                          <React.Fragment key={val}>
                            <motion.span 
                              className={cn(
                                "font-num font-black px-2 py-1 rounded-lg text-xs border",
                                previewTarget === val
                                  ? "bg-slate-900 border-amber-500/40 text-amber-400 shadow-md"
                                  : "bg-slate-900/50 border-slate-700/50 text-slate-500"
                              )}
                              animate={{ scale: previewTarget === val ? 1.15 : 1 }}
                              transition={{ duration: 0.2 }}
                            >
                              {val}
                            </motion.span>
                            {idx < 4 && <span className="text-slate-600 font-bold text-[10px]">➔</span>}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {step === 23 && (
                  <div className="flex flex-col items-center justify-center my-6 gap-6 w-full max-w-xs mx-auto">
                    {/* Simulated Capsule (8 VS 12) */}
                    <div className="w-full aspect-[3.5/1] rounded-full bg-[#f8fafc] border border-slate-100/80 shadow-[0_4px_12px_rgba(0,0,0,0.05)] flex items-center justify-between px-8">
                      <span className="text-3xl font-num font-black text-slate-400">4</span>
                      <span className="text-sm font-num font-bold text-slate-300">VS</span>
                      <span className="text-3xl font-num font-black text-sky-600">7</span>
                    </div>

                    {/* Left and Right blue buttons */}
                    <div className="flex justify-between w-full px-6 relative items-center">
                      {/* Left Button (unpressed) */}
                      <div className="w-16 h-16 rounded-full bg-gradient-to-b from-sky-400 to-sky-500 border-b-[4px] border-sky-600 shadow-md flex items-center justify-center">
                        <div className="w-5 h-5 rounded-full bg-white shadow-sm" />
                      </div>

                      {/* Animated Pointer / Click Hand indicator */}
                      <motion.div 
                        className="absolute right-[40px] w-5 h-5 rounded-full border border-amber-400 bg-amber-400/40 z-30 pointer-events-none flex items-center justify-center"
                        animate={{
                          scale: [1, 0.85, 1],
                          y: [0, 4, 0]
                        }}
                        transition={{
                          duration: 1.5,
                          repeat: Infinity,
                          ease: "easeInOut"
                        }}
                      >
                        <div className="w-2.5 h-2.5 rounded-full bg-amber-400 animate-ping absolute" />
                        <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                      </motion.div>

                      {/* Right Button (pressed / active) */}
                      <motion.div 
                        className="w-16 h-16 rounded-full bg-gradient-to-b from-sky-400 to-sky-500 shadow-md flex items-center justify-center border-b-[0px] translate-y-[4px] border-sky-600"
                        animate={{
                          boxShadow: ["0 0 0px rgba(14,165,233,0)", "0 0 16px rgba(14,165,233,0.6)", "0 0 0px rgba(14,165,233,0)"]
                        }}
                        transition={{
                          duration: 1.5,
                          repeat: Infinity
                        }}
                      >
                        <div className="w-5 h-5 rounded-full bg-white shadow-sm" />
                      </motion.div>
                    </div>
                  </div>
                )}

                {step === 24 && (
                  <div className="flex flex-col items-center justify-center my-6 gap-4 w-full max-w-xs mx-auto">
                    {/* Example 1: 8 vs 2 + 5 */}
                    <div className="flex flex-col items-center w-full">
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 align-self-start">Example 1</span>
                      <div className="w-full aspect-[4/1] rounded-full bg-[#f8fafc] border border-slate-100/80 shadow-md flex items-center px-6 relative overflow-hidden">
                        <span className="flex-1 text-left text-2xl font-num font-black text-sky-600 pl-4">8</span>
                        <span className="text-xs font-num font-bold text-slate-300 mx-2 text-center">VS</span>
                        <span className="flex-1 text-right text-2xl font-num font-black text-slate-400 pr-4">2 + 5</span>
                      </div>
                    </div>

                    {/* Example 2: 2 × 3 vs 7 */}
                    <div className="flex flex-col items-center w-full">
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 align-self-start">Example 2</span>
                      <div className="w-full aspect-[4/1] rounded-full bg-[#f8fafc] border border-slate-100/80 shadow-md flex items-center px-6 relative overflow-hidden">
                        <span className="flex-1 text-left text-2xl font-num font-black text-slate-400 pl-4">2 × 3</span>
                        <span className="text-xs font-num font-bold text-slate-300 mx-2 text-center">VS</span>
                        <span className="flex-1 text-right text-2xl font-num font-black text-sky-600 pr-4">7</span>
                      </div>
                    </div>
                  </div>
                )}

                {step === 32 && (
                  <div className="flex flex-col items-center justify-center my-6 gap-2 w-full">
                    {/* Star Ticket Visual */}
                    <motion.div 
                      className="relative w-40 h-24 rounded-2xl bg-gradient-to-br from-amber-400 to-yellow-500 shadow-xl border-2 border-yellow-300 flex items-center justify-center overflow-hidden"
                      animate={{ y: [0, -6, 0] }}
                      transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                    >
                      {/* Ticket Notch Left & Right */}
                      <div className="absolute -left-4 w-8 h-8 rounded-full bg-white border-r-2 border-yellow-300 z-10" />
                      <div className="absolute -right-4 w-8 h-8 rounded-full bg-white border-l-2 border-yellow-300 z-10" />
                      
                      {/* Inner Star */}
                      <Star className="w-12 h-12 text-white fill-white drop-shadow-[0_2px_8px_rgba(251,191,36,0.6)] animate-pulse" />
                    </motion.div>
                  </div>
                )}

                {step === 39 && (
                  <div className="flex flex-col items-center justify-center my-6 gap-6 w-full">
                     <div className="flex items-end justify-center gap-6 w-full">
                        <div className="flex flex-col items-center gap-2">
                           <div className="w-16 h-16 relative">
                              <Prism3D color="blue" stars={1} isCorrupted={false} potentials={[{type: 'fever_bonus'}]} isStatic />
                           </div>
                           <div className="flex flex-col items-center">
                              <span className="text-[10px] font-black text-slate-400">1-STAR</span>
                              <span className="text-[8px] font-black text-slate-500 mt-1 uppercase text-center leading-tight">Trait<br/>+<br/>1 Potential</span>
                           </div>
                        </div>
                        
                        <div className="flex flex-col items-center gap-2">
                           <div className="w-20 h-20 relative">
                              <Prism3D color="blue" stars={2} isCorrupted={false} potentials={[{type: 'fever_bonus'}, {type: 'fever_bonus'}]} isStatic />
                           </div>
                           <div className="flex flex-col items-center">
                              <span className="text-[10px] font-black text-slate-400">2-STAR</span>
                              <span className="text-[8px] font-black text-slate-500 mt-1 uppercase text-center leading-tight">Trait<br/>+<br/>2 Potentials</span>
                           </div>
                        </div>

                        <div className="flex flex-col items-center gap-2">
                           <div className="w-24 h-24 relative">
                              <Prism3D color="blue" stars={3} isCorrupted={false} potentials={[{type: 'fever_bonus'}, {type: 'fever_bonus'}, {type: 'fever_bonus'}]} isStatic />
                           </div>
                           <div className="flex flex-col items-center">
                              <span className="text-[10px] font-black text-slate-400">3-STAR</span>
                              <span className="text-[8px] font-black text-slate-500 mt-1 uppercase text-center leading-tight">Trait<br/>+<br/>3 Potentials</span>
                           </div>
                        </div>
                     </div>
                  </div>
                )}

                {step === 40 && (
                  <div className="flex flex-col items-center justify-center my-6 gap-3">
                    <span className="text-slate-400 text-[10px] font-black tracking-widest text-center uppercase mb-1">Trait values increase based on star level</span>
                    <div className="grid grid-cols-2 gap-3 w-full">
                      {[
                        { color: 'bg-[#f43f5e]', name: 'Red', val: 'Fever Score' },
                        { color: 'bg-[#fbbf24]', name: 'Yellow', val: 'Coin Bonus' },
                        { color: 'bg-[#10b981]', name: 'Green', val: 'EXP Bonus' },
                        { color: 'bg-[#38bdf8]', name: 'Blue', val: 'Game Time' },
                        { color: 'bg-[#8b5cf6]', name: 'Violet', val: 'Score Bonus' },
                      ].map((t, i) => (
                        <div key={i} className="flex items-center gap-3 bg-slate-50 p-3 rounded-xl border border-slate-100">
                          <div className={cn("w-8 h-8 rounded-md flex items-center justify-center shadow-sm shrink-0", t.color)}>
                            <Star className="w-4 h-4 text-white fill-current" />
                          </div>
                          <div className="flex flex-col">
                            <span className="text-[10px] font-black text-slate-400 uppercase">{t.name}</span>
                            <span className="text-[11px] font-black text-slate-700 leading-tight">{t.val}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {step === 41 && (
                  <div className="flex flex-col items-center justify-center my-6 gap-3">
                    <div className="grid grid-cols-2 gap-3 w-full">
                      {[
                        { color: '#fbbf24', name: 'Bonus', val: 'GC Multiplier +5%' },
                        { color: '#10b981', name: 'Bonus', val: 'EXP Multiplier +5%' },
                        { color: '#f97316', name: 'Hybrid', val: 'Coin & EXP +3%' },
                        { color: '#6366f1', name: 'Flat', val: 'Reward Coins +30' },
                        { color: '#38bdf8', name: 'Time', val: 'Time Bonus +2s' },
                        { color: '#f43f5e', name: 'Fever', val: 'Fever Score +10%' },
                        { color: '#a855f7', name: 'Score', val: 'Score Bonus +2%' },
                      ].map((t, i) => (
                        <div key={i} className="flex items-center gap-3 bg-slate-50 p-3 rounded-xl border border-slate-100">
                          <div className="w-8 h-8 rounded-md flex items-center justify-center shadow-sm shrink-0 relative overflow-hidden" style={{ backgroundColor: t.color }}>
                             <div className="absolute inset-0 bg-gradient-to-br from-white/30 to-transparent pointer-events-none" />
                          </div>
                          <div className="flex flex-col">
                            <span className="text-[10px] font-black text-slate-400 uppercase">{t.name}</span>
                            <span className="text-[11px] font-black text-slate-700 leading-tight">{t.val}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {step === 42 && (
                  <div className="flex flex-col items-center justify-center my-6 gap-6 w-full">
                    <div className="flex flex-col items-center gap-3 w-full border-b border-slate-100 pb-4">
                       <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Evolve to 2-Star</div>
                       <div className="flex items-center gap-2">
                         <div className="flex gap-2">
                            {[1,2,3].map(i => (
                               <div key={i} className="w-12 h-12">
                                 <Prism3D color="blue" stars={1} isCorrupted={false} potentials={[{type: 'fever_bonus'}]} isStatic />
                               </div>
                            ))}
                         </div>
                         <span className="font-num font-black text-slate-300 mx-1">→</span>
                         <div className="w-16 h-16">
                           <Prism3D color="blue" stars={2} isCorrupted={false} potentials={[{type: 'fever_bonus'}, {type: 'fever_bonus'}]} isStatic />
                         </div>
                       </div>
                    </div>
                    
                    <div className="flex flex-col items-center gap-3 w-full pt-2">
                       <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Evolve to 3-Star</div>
                       <div className="flex items-center gap-2">
                         <div className="flex gap-2">
                            {[1,2,3].map(i => (
                               <div key={`2s-${i}`} className="w-14 h-14">
                                 <Prism3D color="blue" stars={2} isCorrupted={false} potentials={[{type: 'fever_bonus'}, {type: 'fever_bonus'}]} isStatic />
                               </div>
                            ))}
                         </div>
                         <span className="font-num font-black text-slate-300 mx-1">→</span>
                         <div className="w-20 h-20">
                           <Prism3D color="blue" stars={3} isCorrupted={false} potentials={[{type: 'fever_bonus'}, {type: 'fever_bonus'}, {type: 'fever_bonus'}]} isStatic />
                         </div>
                       </div>
                    </div>
                  </div>
                )}

                {step === 43 && (
                  <div className="flex flex-col items-center justify-center my-6 gap-6 w-full">
                    
                    {/* Method 1: Same Color Fodder */}
                    <div className="flex flex-col items-center gap-2 w-full border-b border-slate-100 pb-4">
                       <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Reroll: Same Color Fodder<br/>(Requires 1)</div>
                       <div className="flex items-center gap-4 mt-2">
                         {/* Base Core */}
                         <div className="flex flex-col items-center relative">
                           <div className="text-[8px] font-black bg-amber-400 text-white px-1.5 rounded-full absolute -top-3 z-10 shadow-sm">BASE</div>
                           <div className="w-16 h-16">
                             <Prism3D color="blue" stars={3} isCorrupted={false} potentials={[{type: 'time_bonus'}, {type: 'time_bonus'}, {type: 'time_bonus'}]} isStatic />
                           </div>
                         </div>
                         <span className="font-num font-black text-slate-300 text-xl">+</span>
                         {/* Fodder Core */}
                         <div className="flex flex-col items-center relative">
                           <div className="text-[8px] font-black bg-slate-400 text-white px-1.5 rounded-full absolute -top-3 z-10 shadow-sm">FODDER</div>
                           <div className="w-12 h-12">
                             <Prism3D color="blue" stars={3} isCorrupted={false} potentials={[{type: 'fever_bonus'}, {type: 'fever_bonus'}, {type: 'fever_bonus'}]} isStatic />
                           </div>
                           <span className="text-[10px] font-black text-slate-400 mt-1">x1</span>
                         </div>
                       </div>
                    </div>

                    {/* Method 2: Different Color Fodder */}
                    <div className="flex flex-col items-center gap-2 w-full pt-2">
                       <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Reroll: Different Color Fodder<br/>(Requires 2)</div>
                       <div className="flex items-center gap-4 mt-2">
                         {/* Base Core */}
                         <div className="flex flex-col items-center relative">
                           <div className="text-[8px] font-black bg-amber-400 text-white px-1.5 rounded-full absolute -top-3 z-10 shadow-sm">BASE</div>
                           <div className="w-16 h-16">
                             <Prism3D color="blue" stars={3} isCorrupted={false} potentials={[{type: 'time_bonus'}, {type: 'time_bonus'}, {type: 'time_bonus'}]} isStatic />
                           </div>
                         </div>
                         <span className="font-num font-black text-slate-300 text-xl">+</span>
                         {/* Fodder Cores */}
                         <div className="flex flex-col items-center relative">
                           <div className="text-[8px] font-black bg-slate-400 text-white px-1.5 rounded-full absolute -top-3 z-10 shadow-sm">FODDERS</div>
                           <div className="flex -space-x-4">
                             <div className="w-12 h-12">
                               <Prism3D color="red" stars={3} isCorrupted={false} potentials={[{type: 'gc_multi'}, {type: 'gc_multi'}, {type: 'gc_multi'}]} isStatic />
                             </div>
                             <div className="w-12 h-12">
                               <Prism3D color="green" stars={3} isCorrupted={false} potentials={[{type: 'gc_multi'}, {type: 'gc_multi'}, {type: 'gc_multi'}]} isStatic />
                             </div>
                           </div>
                           <span className="text-[10px] font-black text-slate-400 mt-1">x2</span>
                         </div>
                       </div>
                    </div>

                  </div>
                )}

                {(data.action === 'click' || data.action === 'multi-click') && step !== 20 && (
                  <div className="flex items-center gap-2 text-amber-500 mb-0">
                    <motion.div
                      className="w-2 h-2 rounded-full bg-current"
                      animate={{ opacity: [1, 0.3, 1] }}
                      transition={{ duration: 1, repeat: Infinity }}
                    />
                    <span className="text-[10px] font-black uppercase tracking-widest">
                      Tap the highlighted area
                    </span>
                  </div>
                )}

                {data.action === 'drag' && (
                  <div className="flex items-center gap-2 text-amber-500 mb-0">
                    <motion.div
                      animate={{ x: [0, 6, 0] }}
                      transition={{ duration: 0.9, repeat: Infinity }}
                    >
                      {/* Mini drag icon */}
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                        <path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                      </svg>
                    </motion.div>
                    <span className="text-[10px] font-black uppercase tracking-widest">
                      Drag the highlighted area
                    </span>
                  </div>
                )}
              </motion.div>

            </>
          )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
